import { tenantIdFromPathname } from "./tenant";

/**
 * ログイン後に元の画面へ戻すための退避先（PRODUCT_DEV-916）
 *
 * ## なぜ要るのか
 *
 * 認証ポータルはログインが終わると **DDB Products の `productUrl`（ルート）** に
 * `#authCode` を付けて戻す。戻り先を覚える仕組みがポータルに無いので、商談詳細の
 * URL から入ってもトップに着く。ポータルは変えず、**プロダクトのオリジンの中で**
 * 開こうとした URL を退避し、`#authCode` で戻ってきた直後に復元する。
 *
 * dev-lab が PRODUCT_DEV-548 / 565 / 814 で先に同じことをしていて、そこで踏んだ罠を
 * そのまま持ち込んでいる（復元は往復直後だけ・値は毎回消す・ログアウトで消す）。
 *
 * ## なぜ sessionStorage か
 *
 * ポータルのログインは全経路が同じタブの中で終わる（行きは redirect、戻りは
 * `location.href`。ポップアップも外部 IdP も無い）。localStorage にすると、
 * 別のタブや前にログインしていた人の退避値が混ざる。
 *
 * 取り出しても 15 分を過ぎた値は捨てる。同じタブでポータルから別プロダクトを選び、
 * あとでこのプロダクトに来たときに古い戻り先へ飛ばさないため。
 *
 * ## PRODUCT_DEV-1129: ポータルからも返す
 *
 * ログイン画面で 15 分を超えて置かれると、退避が切れてトップに着いていた（席を外している
 * 間にセッションが切れ、戻ってからログインする使い方で起きる。dev で再現）。いまは
 * 戻り先をポータルへの URL の `returnTo` にも載せ、ポータルが `#authCode=…&returnTo=…`
 * で返す。退避はポータルが返さないとき（反映前など）の予備で、どちらも `isSafeReturnPath`
 * を通してから移る。
 */

/** 退避値の寿命。ログイン（MFA・パスキー込み）を終えるには十分で、翌日まで残らない長さ。 */
export const RETURN_TO_TTL_MS = 15 * 60 * 1000;

/** プロダクトごとのキー。Unizo と Minizo のようにトークン保存名が違えば混ざらない。 */
export const returnToKey = (tokenStoreName: string): string =>
  `${tokenStoreName}:returnTo`;

/**
 * PRODUCT_DEV-1129: セッション切れの再ログインで、プロダクトと認証ポータルの間で受け渡す
 * パラメータの名前。**両側（`loaders/auth-loader.ts` と portal の LoginPage）がここを読む。**
 *
 * - `lastTenantId`（プロダクト → ポータルのクエリ）: 直前に使っていたテナント。「入りたい」
 *   という希望で、所属していなければポータルは普通の選択画面を出す（`tenantId` の絞り込みとは別）
 * - `returnTo`（プロダクト → ポータルのクエリ、ポータル → プロダクトの `#` の後ろ）:
 *   見ていた画面の**パスだけ**（クエリは載せない。検索語などがポータルのログに残るため）。
 *   ポータルは、`lastTenantId` と違うテナントで入ったときは返さない
 */
export const PORTAL_RESUME_PARAMS = {
  lastTenantId: "lastTenantId",
  returnTo: "returnTo",
} as const;

/** 戻り先からクエリを外す（認証ポータルへ渡すのはパスだけ。PRODUCT_DEV-1129）。 */
export const pathWithoutQuery = (path: string): string => {
  const i = path.indexOf("?");
  return i < 0 ? path : path.slice(0, i);
};

/** 戻り先の長さの上限。URL に載せて往復させるので、極端に長いものは落とす。 */
export const MAX_RETURN_PATH_LENGTH = 2000;

/**
 * 同じオリジンの中のパスだけを通す。
 *
 * `//evil.com` はプロトコル相対で別ホストになる。`/\evil.com` もブラウザは `//` と
 * 同じに解釈する。制御文字はブラウザが URL から取り除くので、`/\t/evil.com` が
 * `//evil.com` に化ける。どれも弾く。
 *
 * PRODUCT_DEV-1129: 認証ポータルも同じ関数で検査する（規則を2か所に持たない）。
 */
export const isSafeReturnPath = (path: string): boolean =>
  path.length <= MAX_RETURN_PATH_LENGTH &&
  path.startsWith("/") &&
  !path.startsWith("//") &&
  !path.includes("\\") &&
  // eslint-disable-next-line no-control-regex
  !/[\u0000-\u001f\u007f]/.test(path);

/** エラーページへは戻さない（戻っても同じエラーを見せるだけ）。 */
const ERROR_PAGE = /(^|\/)(403|404)(\/|$)/;

/**
 * 退避する値を作る。退避しないものは `null`。
 *
 * **先頭のテナントIDは外す。** `/<tenantId>/` はエイリアスログインの入口で、
 * 認証済みで来ると root-loader が `/` へ戻す。テナント付きのまま退避すると
 * 復元のたびにパスが捨てられる。テナントはポータルへ飛ぶ URL の側に残るので、
 * ログイン画面はそのテナントのものが出る。
 */
export const toReturnPath = (
  pathname: string,
  search: string,
): string | null => {
  const tenantId = tenantIdFromPathname(pathname);
  const rest = tenantId
    ? pathname.slice(pathname.indexOf(tenantId) + tenantId.length) || "/"
    : pathname;
  if (rest === "/" || ERROR_PAGE.test(rest)) return null;
  const path = `${rest}${search}`;
  return isSafeReturnPath(path) ? path : null;
};

// sessionStorage が使えない環境（プライベートブラウズの一部・埋め込み）では
// 退避を諦めて従来どおりトップに着かせる。ここで例外を出すと認証そのものが止まる。
const read = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};
const write = (key: string, value: string): void => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* 退避なしで通常フロー */
  }
};

/** 退避を消す。ログアウト時にも呼ぶ（残すと次にログインした人が前の人の画面へ飛ぶ）。 */
export const clearReturnTo = (key: string): void => {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* noop */
  }
};

export const saveReturnTo = (
  key: string,
  path: string | null,
  now: number = Date.now(),
): void => {
  if (!path || !isSafeReturnPath(path)) return;
  write(key, JSON.stringify({ path, savedAt: now }));
};

/**
 * 退避値を取り出す。**読んだら必ず消す**（PRODUCT_DEV-565: 残すと後のリロードで
 * 本来の画面にいる人を古い戻り先へ飛ばす）。期限切れ・壊れた値・安全でない値は `null`。
 */
export const takeReturnTo = (
  key: string,
  now: number = Date.now(),
): string | null => {
  const raw = read(key);
  if (raw === null) return null;
  clearReturnTo(key);
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const { path, savedAt } = parsed as { path?: unknown; savedAt?: unknown };
    if (typeof path !== "string" || typeof savedAt !== "number") return null;
    if (now - savedAt > RETURN_TO_TTL_MS) return null;
    return isSafeReturnPath(path) ? path : null;
  } catch {
    return null;
  }
};
